import { Deduction } from "@/api/deduction.tsx"
import { api_fetch } from "@/api/client.tsx"
import { Err, Ok, Result, Option } from "@/utils"

export interface Dispute {
  id: string
  org_id: string
  deduction_id?: string

  job_id?: string
  created_at: string
  updated_at: string
  source: string

  user_id: string
  type: DisputeType
  amount: number

  dispute_type_description: string
  message: string
  dispute_file_s3_uris: string[]
  sent_at: Option<string>
  thread_id: Option<string>
  metadata?: DisputeMetadata | null
}

export interface KsolveNoteMeta {
  id: number
  created_by?: string | null
  created_on: string
}

export interface DisputeMetadata {
  /** "ksolve" for a dispute the brand filed directly in KeHE's K-Solve. */
  filed_in?: string
  /** The K-Solve notes the dispute's message was read from, oldest first. */
  ksolve_notes?: KsolveNoteMeta[]
  [key: string]: unknown
}

/** Messages the KeHE sync wrote on K-Solve disputes before it read their text. */
const KSOLVE_PLACEHOLDER_MESSAGES = ["created externally, directly in ksolve", "created externally from ksolve"]

/** True for a dispute the brand filed directly in KeHE's K-Solve rather than through MarginWiz. */
export function is_filed_in_ksolve(dispute: Dispute): boolean {
  return dispute.metadata?.filed_in === "ksolve" || KSOLVE_PLACEHOLDER_MESSAGES.includes(dispute.message)
}

/** True when a K-Solve dispute's message holds the brand's own text, read from K-Solve's notes. */
export function has_ksolve_dispute_text(dispute: Dispute): boolean {
  return (dispute.metadata?.ksolve_notes?.length ?? 0) > 0 && !KSOLVE_PLACEHOLDER_MESSAGES.includes(dispute.message)
}

export enum DisputeType {
  Backup = "backup",
  Chargeback = "chargeback",
  EarlyPay = "earlypay",
  /** The distributor paid an invoice short with no chargeback explaining the gap; asked back from AP. */
  ShortPay = "shortpay",
  Unknown = "unknown",
}

export interface DisputeJson extends Dispute {
  error?: string
}

export interface FileDisputeData {
  deduction_id: string
  type: DisputeType
  message: string
}

function build_dispute_form_inner(data: FileDisputeData): FormData {
  let formData = new FormData()
  formData.append("deduction_id", data.deduction_id)
  formData.append("dispute_type", data.type)
  formData.append("message", data.message)
  return formData
}

export interface RequestBackupFormOptions {
  is_rerequest?: boolean
}

export function build_request_backup_form(deduction: Deduction, options: RequestBackupFormOptions = {}): FormData {
  let message = `Please provide backup for this charge. The deduction invoice number is ${deduction.invoice_number}.`
  let form = build_dispute_form_inner({
    deduction_id: deduction.id,
    type: DisputeType.Backup,
    message,
  })
  if (options.is_rerequest) {
    form.append("is_rerequest", "true")
  }
  return form
}

export interface FileDispute {
  deduction: Deduction
  message: string
  dispute_amount: number
  files: File[]
  existing_file_uris: string[] // concatenated string of existing file uris, comma separated
  /** Extra addresses to CC on an emailed dispute, on top of what the server adds itself. */
  cc?: string[]
}

/** Where a dispute filed from the drawer goes — `GET /deduction/{id}/dispute-recipients`. */
export interface DisputeRecipients {
  /** `email`: MarginWiz mails it to `to`/`cc`. `portal`: filed through the customer's portal integration. `recorded`: only logged here. */
  channel: "email" | "portal" | "recorded"
  to: string | null
  /** What the server adds on its own (Core-Mark's purchasing manager off the fee notice, the Eby-Brown departments). */
  cc: string[]
  cc_note: string | null
  /** Why the send would fail (no address configured) — shown instead of a recipient. */
  error: string | null
  /** Why this dispute cannot be filed at all (an earlier UNFI claim is still open in its portal). The drawer shows it and holds Submit. */
  refusal: string | null
  /** Set when this filing goes to UNFI as an appeal of a denied claim. */
  portal_appeal: { claim: string; opens_at: string } | null
  /** Whether the drawer offers the shipment block — only for a Core-Mark late fee. */
  shipment_lookup: boolean
}

export async function fetch_dispute_recipients(deduction_id: string, type: DisputeType) {
  return api_fetch<DisputeRecipients>(`/deduction/${deduction_id}/dispute-recipients?dispute_type=${type}`)
}

/**
 * Split a typed list of addresses (`"a@x.com, b@y.com; c@z.com"`) into trimmed, de-duplicated
 * entries that look like emails. Mirror of `db::dispute::parse_email_list`.
 */
export function parse_email_list(raw: string): string[] {
  const out: string[] = []
  for (const part of raw.split(/[,;\s]+/)) {
    const addr = part.trim().replace(/^[<"]+|[>"]+$/g, "")
    if (!addr || !addr.includes("@") || addr.startsWith("@") || addr.endsWith("@")) continue
    if (!out.some((e: string) => e.toLowerCase() === addr.toLowerCase())) out.push(addr)
  }
  return out
}

/** Add `extra` to a CC list without duplicates (case-insensitive) and never the `to` address. */
export function add_cc(existing: string[], extra: string[], to: string | null): string[] {
  const out = [...existing]
  for (const addr of extra) {
    if (to && addr.toLowerCase() === to.toLowerCase()) continue
    if (!out.some((e: string) => e.toLowerCase() === addr.toLowerCase())) out.push(addr)
  }
  return out
}

export interface TrackingLink {
  number: string
  url: string | null
}

export interface ShipmentPackage {
  tracking_number: string | null
  weight: number | null
  /** `L x W x H` as entered on the carrier sublist. */
  dimensions: string | null
  /** Carton / package description on a generic (freight) package. */
  description: string | null
  delivery_confirmation: string | null
}

/** A NetSuite custom body field worth showing ("Scheduled with coyote: Yes"). */
export interface ShipmentCustomField {
  key: string
  label: string
  value: string
}

export type ShipCarrier = "ups" | "fed_ex" | "usps" | "freight"

/** One item fulfillment read out of the brand's NetSuite for the deduction's PO. */
export interface DeductionShipment {
  sales_order_id: string
  sales_order_number: string | null
  po_number: string | null
  fulfillment_id: string
  fulfillment_number: string | null
  /** As NetSuite prints it (`M/D/YYYY`), not ISO. */
  ship_date: string | null
  status: string | null
  ship_method: string | null
  memo: string | null
  carrier: ShipCarrier | null
  tracking_numbers: string[]
  package_count: number
  packages: ShipmentPackage[]
  ship_to: string | null
  total_weight: number | null
  custom_fields: ShipmentCustomField[]
  /** The fulfillment in the NetSuite UI, for a signed-in person. */
  url: string | null
  tracking: TrackingLink[]
}

/** The sales order the PO landed on. */
export interface ShipmentSalesOrder {
  id: string
  number: string | null
  po_number: string | null
  customer: string | null
  order_date: string | null
  status: string | null
  requested_delivery_date: string | null
  ship_date: string | null
  actual_ship_date: string | null
  ship_method: string | null
  ship_to: string | null
  location: string | null
  memo: string | null
  total: number | null
  linked_tracking_numbers: string[]
  custom_fields: ShipmentCustomField[]
  url: string
}

/** A File Cabinet file named after the order (pallet photos, a saved POD, a BOL). Linked, not synced. */
export interface ShipmentFile {
  id: string
  name: string
  filetype: string | null
  filesize: string | null
  createddate: string | null
  url: string | null
  open_url: string | null
}

export interface OrderShipments {
  order: ShipmentSalesOrder
  fulfillments: DeductionShipment[]
  files: ShipmentFile[]
}

/** `GET /deduction/{id}/shipments` */
export interface DeductionShipments {
  po_number: string | null
  netsuite_connected: boolean
  orders: OrderShipments[]
  /** Every fulfillment across the orders. */
  shipments: DeductionShipment[]
  /** Ready-to-paste sentence(s) for the dispute message; empty when nothing was found. */
  message_snippet: string
  note: string | null
}

export function carrier_label(carrier: ShipCarrier): string {
  switch (carrier) {
    case "ups":
      return "UPS"
    case "fed_ex":
      return "FedEx"
    case "usps":
      return "USPS"
    case "freight":
      return "freight"
  }
}

/** "Shipped 8/6/2026 via UPS® Ground · 9 packages" for a fulfillment. */
export function shipment_headline(s: DeductionShipment): string {
  let line = "Shipped"
  if (s.ship_date) line += ` ${s.ship_date}`
  if (s.ship_method && s.ship_method !== "Custom") line += ` via ${s.ship_method}`
  else if (s.carrier) line += ` via ${carrier_label(s.carrier)}`
  if (s.package_count) line += ` · ${s.package_count} package${s.package_count === 1 ? "" : "s"}`
  return line
}

/** Human file size from NetSuite's byte-count string. */
export function format_file_size(bytes: string | null): string {
  const n = Number(bytes)
  if (!bytes || !Number.isFinite(n) || n <= 0) return ""
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export async function fetch_deduction_shipments(deduction_id: string) {
  return api_fetch<DeductionShipments>(`/deduction/${deduction_id}/shipments`)
}

/** Append `snippet` to the message as its own paragraph, once. */
export function append_to_message(message: string, snippet: string): string {
  const s = snippet.trim()
  if (!s || message.includes(s)) return message
  const base = message.trimEnd()
  return base ? `${base}\n\n${s}` : s
}

export function build_dispute_form(d: FileDispute): FormData {
  let type = d.deduction.kind === "deduction" ? DisputeType.Chargeback : DisputeType.EarlyPay
  let form = build_dispute_form_inner({
    deduction_id: d.deduction.id,
    type,
    message: d.message,
  })
  form.append("dispute_amount", d.dispute_amount.toString())
  for (const f of d.files) {
    form.append("files", f)
  }
  form.append("existing_files", JSON.stringify(d.existing_file_uris))
  if (d.cc && d.cc.length > 0) {
    form.append("cc", JSON.stringify(d.cc))
  }
  return form
}

/** The message to show for a failed dispute submit, whatever the body is (a proxy's 502/504 page is HTML). */
export function dispute_error_message(status: number, body: string): string {
  try {
    const message = JSON.parse(body)?.message
    if (typeof message === "string" && message.trim()) return message
  } catch {
    // Not JSON: fall through to the status.
  }
  return `The dispute was not filed (server error ${status}). Please try again.`
}

/** Never throws: a network failure or a non-JSON error comes back as `Err`, so the drawer can reset. */
export async function submit_dispute(form: FormData): Promise<Result<Dispute, Error>> {
  let res: Response
  try {
    res = await fetch("/api/dispute", {
      method: "POST",
      credentials: "same-origin",
      body: form,
    })
  } catch (e) {
    console.error("Failed to submit dispute", e)
    return Err(new Error("The dispute was not filed: could not reach the server. Please try again."))
  }
  let text = await res.text().catch(() => "")
  if (res.ok) {
    // The server answers `null`; the dispute is filed whatever the body says.
    let body: unknown = null
    try {
      body = text ? JSON.parse(text) : null
    } catch {
      // Filed all the same.
    }
    return Ok(body as Dispute)
  }
  let message = dispute_error_message(res.status, text)
  console.error("Failed to submit dispute", message)
  return Err(new Error(message))
}

export async function bulk_submit_dispute(forms: FormData[]): Promise<Result<number, Error>> {
  let i = 0
  for (const form of forms) {
    let res = await submit_dispute(form)
    if (res.ok) {
      i++
    } else {
      return res
    }
  }
  return Ok(i)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("parse_email_list", () => {
    it("splits on commas, semicolons and whitespace and dedupes case-insensitively", () => {
      expect(parse_email_list(" Taryn.Sanders@pfgc.com, buyer@pfgc.com;taryn.sanders@pfgc.com <other@x.com> nonsense @ ")).toEqual([
        "Taryn.Sanders@pfgc.com",
        "buyer@pfgc.com",
        "other@x.com",
      ])
      expect(parse_email_list("")).toEqual([])
    })
  })

  describe("add_cc", () => {
    it("never adds the recipient or a duplicate", () => {
      expect(add_cc(["a@x.com"], ["A@x.com", "IPC-VendorSupport@pfgc.com", "b@y.com"], "ipc-vendorsupport@pfgc.com")).toEqual([
        "a@x.com",
        "b@y.com",
      ])
    })
  })

  describe("shipment_headline / format_file_size", () => {
    const base: DeductionShipment = {
      sales_order_id: "1", sales_order_number: "3623969", po_number: "0653946000", fulfillment_id: "2", fulfillment_number: "3553707",
      ship_date: "8/6/2026", status: "Shipped", ship_method: "UPS® Ground", memo: "UPS", carrier: "ups", tracking_numbers: ["1Z"],
      package_count: 9, packages: [], ship_to: null, total_weight: 150, custom_fields: [], url: null, tracking: [],
    }
    it("reads the method, or the carrier when the method is Custom", () => {
      expect(shipment_headline(base)).toBe("Shipped 8/6/2026 via UPS® Ground · 9 packages")
      expect(shipment_headline({ ...base, ship_method: "Custom", carrier: "freight", package_count: 1 })).toBe("Shipped 8/6/2026 via freight · 1 package")
    })
    it("formats sizes", () => {
      expect(format_file_size("8047829")).toBe("7.7 MB")
      expect(format_file_size("44786")).toBe("44 KB")
      expect(format_file_size(null)).toBe("")
    })
  })

  describe("dispute_error_message", () => {
    it("uses the server's message, else names the status", () => {
      expect(dispute_error_message(400, '{"message":"Already disputed"}')).toBe("Already disputed")
      expect(dispute_error_message(502, "<html>Bad Gateway</html>")).toBe(
        "The dispute was not filed (server error 502). Please try again.",
      )
      expect(dispute_error_message(504, "")).toBe("The dispute was not filed (server error 504). Please try again.")
    })
  })

  describe("append_to_message", () => {
    it("adds the snippet as a paragraph once", () => {
      const once = append_to_message("Please repay.", "UPS tracking: 1Z1.")
      expect(once).toBe("Please repay.\n\nUPS tracking: 1Z1.")
      expect(append_to_message(once, "UPS tracking: 1Z1.")).toBe(once)
      expect(append_to_message("", "x")).toBe("x")
      expect(append_to_message("m", "  ")).toBe("m")
    })
  })
}
